import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const FloatingVideo = (props) => {
    const { src, opened, onCloseRequest } = props;

    const videoRef = useRef(null);
    const contentRef = useRef(null);

    const [isOpened, setIsOpened] = useState(Boolean(opened));
    const [isMinimized, setIsMinimized] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    // isMuted = true, что бы браузер воспроизвел видео, иначе он не дает воспроизвести с autoPlay и со звуком
    const [isMuted, setIsMuted] = useState(true);
    const [progress, setProgress] = useState(0);
    const [volume, setVolume] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [videoRatio, setVideoRatio] = useState(null);

    const formatTime = (time) => {
        if (!time || Number.isNaN(time)) {
            return "0:00";
        }

        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);

        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    const handleClose = () => {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        video.pause();
        video.removeAttribute("src");
        video.load();

        setIsOpened(false);
        setIsPlaying(false);
        setProgress(0);
        setVolume(0);
        setDuration(0);
        setCurrentTime(0);
        setVideoRatio(null);

        onCloseRequest?.();
    };

    const toggleFullscreen = async () => {
        const content = contentRef.current;
        const video = videoRef.current;

        if (!content) {
            return;
        }

        setIsMinimized(false);

        try {
            if (!document.fullscreenElement) {
                await content.requestFullscreen();

                setIsFullscreen(true);
            } else {
                await document.exitFullscreen();

                setIsFullscreen(false);
            }
        } catch {
            // fallback для iPhone/iPad Safari
            if (video?.webkitEnterFullscreen) {
                video.webkitEnterFullscreen();
            }
        }
    };

    const togglePlay = async () => {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        if (video.paused) {
            try {
                await video.play();

                setIsPlaying(true);
            } catch {
                setIsPlaying(false);
            }
        } else {
            video.pause();

            setIsPlaying(false);
        }
    };

    const toggleMute = () => {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        video.muted = !video.muted;
        video.volume = video.muted ? 0 : 0.5;

        setIsMuted(video.muted);
        setVolume(video.volume);
    };

    const handleLoadedMetadata = () => {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        setDuration(video.duration);

        if (video.videoWidth && video.videoHeight) {
            setVideoRatio(video.videoWidth / video.videoHeight);
        }
    };

    const handleTimeUpdate = () => {
        const video = videoRef.current;

        if (!video || !video.duration) {
            return;
        }

        setCurrentTime(video.currentTime);
        setProgress((video.currentTime / video.duration) * 100);
    };

    const handleProgressChange = (event) => {
        const video = videoRef.current;

        if (!video || !video.duration) {
            return;
        }

        const value = Number(event.target.value);

        video.currentTime = (value / 100) * video.duration;

        setProgress(value);
    };

    const handleVolumeChange = (event) => {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        const value = Number(event.target.value);

        video.muted = value === 0;
        video.volume = value;

        setIsMuted(video.muted);
        setVolume(value);
    };

    const toggleMinimize = () => {
        if (isFullscreen) {
            toggleFullscreen();
        }

        setIsMinimized((prev) => !prev);
    };

    useEffect(() => {
        setIsOpened(Boolean(opened));
    }, [opened]);

    useEffect(() => {
        if (!isOpened) {
            return;
        }

        const handleKeyDown = (event) => {
            if (event.code !== "Space") {
                return;
            }

            if (event.repeat) {
                return;
            }

            const target = event.target;
            const tagName = target?.tagName?.toLowerCase();

            const isInteractiveElement =
                target?.isContentEditable || ["input", "textarea", "select", "button"].includes(tagName);

            if (isInteractiveElement) {
                return;
            }

            event.preventDefault();

            togglePlay();
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpened, togglePlay]);

    if (!isOpened) {
        return null;
    }

    return (
        <div
            className={cn("floating-video__box", { minimized: isMinimized })}
            style={videoRatio ? { "--floating-video-ratio": videoRatio } : undefined}
        >
            <div ref={contentRef} className="floating-video__content">
                <video
                    ref={videoRef}
                    className="floating-video__player"
                    src={src}
                    autoPlay
                    muted={isMuted}
                    playsInline
                    onClick={togglePlay}
                    onLoadedMetadata={handleLoadedMetadata}
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                />
                <div className="floating-video__controls">
                    <div className="floating-video__control-top">
                        <button
                            type="button"
                            className="floating-video__control-btn icon-uEA07-delite"
                            onClick={handleClose}
                        />
                    </div>
                    <div className="floating-video__control-bottom">
                        <button
                            type="button"
                            className={cn("floating-video__control-btn play-btn audio__play", {
                                "audio__play--start": isPlaying,
                                "audio__play--stop": !isPlaying,
                            })}
                            onClick={togglePlay}
                        />
                        <div className="floating-video__time-text">
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </div>
                        <input
                            className="floating-video__input"
                            type="range"
                            min="0"
                            max="100"
                            value={progress}
                            onChange={handleProgressChange}
                        />
                        {!isMinimized && (
                            <div className="floating-video__volume">
                                <div
                                    className={cn("floating-video__volume-icon", {
                                        "icon-uEAA1-volume-mute": isMuted,
                                        "icon-uEAA0-volume-up": !isMuted,
                                    })}
                                    onClick={toggleMute}
                                />
                                <input
                                    className="floating-video__input"
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={volume}
                                    onChange={handleVolumeChange}
                                />
                            </div>
                        )}
                        {isMinimized && (
                            <button
                                type="button"
                                className={cn("floating-video__control-btn", {
                                    "icon-uEAA1-volume-mute": isMuted,
                                    "icon-uEAA0-volume-up": !isMuted,
                                })}
                                onClick={toggleMute}
                            />
                        )}
                        <button
                            type="button"
                            className={cn("floating-video__control-btn", {
                                "icon-uEAD8-close-window": isMinimized,
                                "icon-uEAD7-open-window": !isMinimized,
                            })}
                            onClick={toggleMinimize}
                        />
                        <button
                            type="button"
                            className={cn("floating-video__control-btn", {
                                "icon-uEADA-fullscreen-exit": isFullscreen,
                                "icon-uEAD9-fullscreen": !isFullscreen,
                            })}
                            onClick={toggleFullscreen}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const FloatingVideoWrapper = (props) => {
    if (props.portalTarget) {
        return createPortal(<FloatingVideo {...props} />, props.portalTarget);
    }

    return <FloatingVideo {...props} />;
};

export default FloatingVideoWrapper;
